import React, { useState } from 'react';
import { Input, Button, Upload, Space, message as antMessage } from 'antd';
import { SendOutlined, PaperClipOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { uploadAttachment } from '../../../services/supportTicketsService';

const { TextArea } = Input;

const QUICK_REPLIES = [
  'Cảm ơn bạn đã liên hệ. Chúng tôi đang xử lý.',
  'Vấn đề đã được ghi nhận. Bộ phận kỹ thuật sẽ phản hồi sớm.',
  'Bạn vui lòng cung cấp thêm thông tin để chúng tôi hỗ trợ tốt hơn.',
];

interface ReplyComposerProps {
  onSubmit: (
    message: string,
    attachmentUrls: Array<{
      url: string;
      file_name: string;
      mime_type: string;
      file_size: number;
      file_type?: string;
    }>
  ) => Promise<void>;
  disabled?: boolean;
}

export const ReplyComposer: React.FC<ReplyComposerProps> = ({
  onSubmit,
  disabled,
}) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleSend = async () => {
    const text = message.trim();
    if (!text && fileList.length === 0) return;
    if (sending) return;

    setSending(true);
    try {
      const attachmentUrls: Array<{
        url: string;
        file_name: string;
        mime_type: string;
        file_size: number;
        file_type?: string;
      }> = [];

      for (const f of fileList) {
        if (f.originFileObj) {
          const up = await uploadAttachment(f.originFileObj);
          attachmentUrls.push({
            url: up.url,
            file_name: up.file_name,
            mime_type: up.mime_type,
            file_size: up.file_size,
            file_type: up.file_type,
          });
        }
      }

      await onSubmit(text || '(Đã gửi file đính kèm)', attachmentUrls);
      setMessage('');
      setFileList([]);
    } catch (e: unknown) {
      const err = e as { message?: string };
      antMessage.error(err.message || 'Gửi thất bại');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ marginBottom: 8 }}>
        <Space wrap size="small">
          {QUICK_REPLIES.map((text, i) => (
            <Button
              key={i}
              size="small"
              onClick={() =>
                setMessage(prev => (prev ? `${prev}\n${text}` : text))
              }
              disabled={disabled}
            >
              {text.slice(0, 25)}…
            </Button>
          ))}
        </Space>
      </div>
      <TextArea
        value={message}
        onChange={e => setMessage(e.target.value)}
        placeholder="Nhập phản hồi..."
        autoSize={{ minRows: 2, maxRows: 6 }}
        disabled={disabled}
        onPressEnter={e => {
          if (!e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
      />
      <div
        style={{
          marginTop: 8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Upload
          fileList={fileList}
          onChange={({ fileList: fl }) => setFileList(fl.slice(-5))}
          beforeUpload={() => false}
          maxCount={5}
          showUploadList={{ showPreviewIcon: false }}
        >
          <Button
            icon={<PaperClipOutlined />}
            disabled={disabled || sending}
            size="small"
          >
            Đính kèm
          </Button>
        </Upload>
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={sending}
          disabled={
            disabled || (message.trim() === '' && fileList.length === 0)
          }
        >
          Gửi
        </Button>
      </div>
    </div>
  );
};
